/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
*/
import React, {
  ComponentProps,
  useLayoutEffect,
  useRef, useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ResponsiveLine} from '@nivo/line';
import {TooltipWrapper} from '@nivo/tooltip';
import {translate} from '@ion/core/src/locale/locale';
import {Skeleton} from '@ion/dls/src/components/skeleton/Skeleton';
import useWindowSize from '@ion/dls/src/hooks/useWindowSize';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

// defines the DatumValue for valid data types of chart axis data.
export type DatumValue = string | number | Date;

// defines the Datum which is used by chart data.
export type Datum = {
  x?: DatumValue | null
  y?: DatumValue | null
  [key: string]: any
}

// defines ChartData which is used by line chart.
export type ChartData = {
  id: string;
  data: Datum[];
  [key: string]: any;
}

// defines Box which is used for margin.
export type Box = {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
}

// defines the AxisProps used for chart axis properties
export interface AxisProps {
  ticksPosition?: 'before' | 'after';
  tickValues?: number;
  tickSize?: number;
  tickPadding?: number;
  tickRotation?: number;
  format?: any;
  legend?: React.ReactNode;
  legendPosition?: 'start' | 'middle' | 'end';
  legendOffset?: number;
  ariaHidden?: boolean;
}

// defines the prop definitions for Line chart
export interface LineChartProps extends ComponentProps<typeof ResponsiveLine> {
  loading: boolean;
  noDataLabel?: string;
  getColor?: any;
  renderTooltipLabel?: Function;
  getPointColor?: any;
  tooltipFontSize?: number;
  containTooltip?: boolean;
  legendTheme?: {
    legend?: {
      text?: {
        fontSize?: number;
        color?: string;
      }
    }
  };
  domainTheme?: {
    domain?: {
      line?: {
        stroke?: string;
        strokeWidth?: number;
      }
    }
  };
}

interface StyleProps {
  tooltipFontSize?: number;
  containTooltip?: boolean;
}

// defines the styles used by Line chart
const useStyles = makeStyles(theme => ({
  container : {
    display        : 'flex',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  },
  noData : {
    ...dlsAlt('typography', 'searchInput'),
    color      : dlsColor('grey4'),
    display    : 'flex',
    alignItems : 'center'
  },
  tooltipContainer : {
    borderRadius    : 3,
    backgroundColor : dlsColor('white'),
    boxShadow       : `2px 2px 10px 0px ${dlsColor('grey3')}`,
    padding         : 10,
    display         : 'flex',
    alignItems      : 'center',
    width           : ({containTooltip}: StyleProps) => containTooltip ? 95 : 'unset'
  },
  tooltip : {
    fontSize : ({tooltipFontSize}: StyleProps) => tooltipFontSize ?? 14
  }
}));

/**
 * Provides the Line Chart component implementation for displaying a given data-set within
 * a Line Chart visualization.
 *
 * ```typescript
 *
 *  import {LineChart} from '@ion/charting/src';
 *
 *  function MyComponent {
 *     return (
 *      <React.Fragment>
 *        <LineChart data={chartData}
 *          loading={true}/>
 *        <LineChart data={reports}
 *          loading={isLoading}
 *          noDataLabel={'No data available to show'}
 *          margin={{top: 30, right: 50, bottom: 50, left: 80}}
 *          renderTooltipLabel={renderTooltipLabel}
 *          getColor={getColor}
 *          getPointColor={getColor}
 *          enableGridX={false}
 *          yScale={{
 *            type    : 'linear',
 *            min     : 0,
 *            max     : 100,
 *            stacked : true,
 *            reverse : false
 *          }}
 *          axisLeft={{
 *            tickSize     : 0,
 *            tickPadding  : 30,
 *            tickRotation : 0,
 *            format       : (value) => (value) => `${value}%`,
 *            tickValues   : 5
 *          }}
 *          gridYValues={[0, 20, 40, 60, 80, 100]} />
 *      </React.Fragment>
 *     );
 *   }
 *
 * ```
 */
const LineChart = ({
  data,
  loading,
  margin,
  tooltipFontSize,
  curve,
  getColor,
  renderTooltipLabel,
  legends,
  xFormat,
  gridXValues,
  gridYValues,
  pointLabel,
  enablePointLabel,
  getPointColor,
  enableSlices,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onClick,
  pointSymbol,
  noDataLabel = translate('dashboard:common.noData'),
  enableGridX = true,
  enableGridY = true,
  isInteractive = true,
  pointSize = 8,
  pointBorderWidth = 2,
  useMesh = true,
  pointLabelYOffset = 12,
  enableCrosshair = false,
  axisTop = null,
  axisRight = null,
  legendTheme,
  domainTheme,
  axisBottom = {
    tickSize     : 5,
    tickPadding  : 15,
    tickRotation : 0
  },
  axisLeft = {
    tickSize     : 0,
    tickPadding  : 20,
    tickRotation : 0
  },
  enablePoints = true,
  yScale = {
    type    : 'linear',
    stacked : true,
    reverse : false
  },
  yFormat = '>-.2f',
  lineWidth = 1,
  xScale = {type: 'point'},
  containTooltip = false
}: LineChartProps) => {
  const {height, width}             = useWindowSize();
  const [offsetSize, setOffsetSize] = useState({
    width  : undefined,
    height : undefined
  });
  const containerRef                 = useRef<HTMLDivElement>(null);
  const classes                      = useStyles({tooltipFontSize, containTooltip});
  const [bottomAxis, setBottomAxis]  = useState(axisBottom);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const containerWidth = container?.clientWidth;
    setOffsetSize({
      height : container?.clientHeight,
      width  : containerWidth
    });
    if (containerWidth <= 500) {
      setBottomAxis({
        ...axisBottom,
        tickRotation : containerWidth < 300 ? -75 : -45
      });
    } else {
      setBottomAxis(axisBottom);
    }
  }, [data, width, height]);

  /**
   * @private
   */
  const CustomTooltip = function(datum) {
    if (renderTooltipLabel && datum) {
      if (containTooltip) {
        const {data : chartdata = []} = data?.[0];
        const isFirstHalf = datum?.point?.index < chartdata?.length / 2;
        return (
          <TooltipWrapper anchor={`${isFirstHalf ? 'right': 'left'}`} position={[0, 0]}>
            <div className={classes.tooltipContainer} >
              <div className={classes.tooltip} >{renderTooltipLabel(datum)}</div>
            </div>
          </TooltipWrapper>
        );
      } else {
        return (
          <div className={classes.tooltipContainer}>
            <div className={classes.tooltip} >{renderTooltipLabel(datum)}</div>
          </div>
        );
      }
    }

    return undefined;
  };

  return (
    <div className={classes.container} ref={containerRef}>
      {!loading ? (
        data?.length ? (
          <ResponsiveLine data={data}
            margin={margin}
            curve={curve}
            lineWidth={lineWidth}
            enablePointLabel={enablePointLabel}
            pointLabel={pointLabel}
            pointLabelYOffset={pointLabelYOffset}
            legends={legends}
            xScale={xScale}
            xFormat={xFormat}
            yScale={yScale}
            yFormat={yFormat}
            enableGridX={enableGridX}
            gridXValues={gridXValues}
            enableGridY={enableGridY}
            gridYValues={gridYValues}
            axisTop={axisTop}
            axisRight={axisRight}
            axisBottom={bottomAxis}
            axisLeft={axisLeft}
            colors={getColor}
            enablePoints={enablePoints}
            pointSize={pointSize}
            pointSymbol={pointSymbol}
            pointColor={getPointColor}
            pointBorderWidth={pointBorderWidth}
            pointBorderColor={{from: 'serieColor'}}
            useMesh={useMesh}
            enableSlices={enableSlices}
            enableCrosshair={enableCrosshair}
            tooltip={CustomTooltip}
            isInteractive={isInteractive}
            onMouseEnter={function(chartData) {
              onMouseEnter?.(chartData);
            }}
            onMouseMove={function(chartData) {
              onMouseMove?.(chartData);
            }}
            onMouseLeave={function(chartData) {
              onMouseLeave?.(chartData);
            }}
            onClick={function(chartData) {
              onClick?.(chartData);
            }}
            theme={{
              fontFamily : 'Roboto',
              fontSize   : legendTheme ? 10 : 12,
              textColor  : dlsColor('grey3'),
              grid       : {
                line : {
                  stroke : dlsColor('grey5')
                }
              },
              axis : {
                ticks : {
                  line : {
                    stroke : dlsColor('grey5')
                  }
                },
                ...(legendTheme ?? {}),
                ...(domainTheme ?? {})
              }
            }} />
        ) : (
          <div className={classes.noData}>
            {noDataLabel}
          </div>
        )) : (
        <Skeleton variant='rect' animation='wave' width={offsetSize.width - 30} height={offsetSize.height - 10} />
      )}
    </div>
  );
};

export default LineChart;
